// ui/presenters/coverageRowPresenter.js
import { i18n } from 'boot/i18n';

import { formatCalendarDate } from 'src/utils/DateUtils';

import { ATTENTION_IS_A_PROBLEM, coverageAttention } from './coverageAttention';

/**
 * Presenter: turns a Coverage into the view model one list row renders.
 *
 * It lives in `presenters/` rather than `components/` because it is not a component, and not in
 * `composables/` because it holds no state and no reactivity — it is a pure mapping from a domain
 * entity to a view model, which is the Presenter role in the interface-adapters ring. Keeping it
 * separate is also what lets the main and ended lists describe a coverage identically while only
 * their chrome differs; the alternative is the same derivation written twice.
 *
 * A row carries the payer and the effective dates, plus — in the main list — one order chip and,
 * only when something needs flagging, one status word. Member id and group number are captured on
 * the coverage and shown in the editor, but they are reference data a biller looks up one record
 * at a time; in a list they crowd out what the overview exists to answer.
 */

// Bounds are stored as 'YYYY-MM-DD' calendar strings; formatCalendarDate localizes them without
// treating them as instants, so the day shown is always the day stored.
const formatPeriod = (coverage) => {
  const start = formatCalendarDate(coverage.period.start);
  const end = formatCalendarDate(coverage.period.end);

  return end === null
    ? i18n.t('coverage.periodOngoing', { start })
    : i18n.t('coverage.period', { start, end });
};

/**
 * @param {import('../../domain/model/Coverage.types').Coverage} coverage
 * @param {Object} context
 * @param {Map<string, string>} context.fundingSourceNameById
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @param {number|null} [context.orderNumber] - billing-order number, or null when it has none
 * @param {boolean} [context.isBillingPaused] - a live BillingIntent against this coverage
 * @param {Object|null} [context.policyholderDemographics] - whoever holds the policy, for readiness
 * @returns {Object}
 */
// eslint-disable-next-line import/prefer-default-export
export function presentCoverageRow(coverage, {
  fundingSourceNameById,
  asOf,
  orderNumber = null,
  isBillingPaused = false,
  policyholderDemographics = null,
}) {
  const attention = coverageAttention(coverage, asOf, {
    isBillingPaused,
    policyholderDemographics,
  });
  const isProblem = ATTENTION_IS_A_PROBLEM[attention] === true;

  return {
    id: coverage.id,
    coverage,
    label: fundingSourceNameById.get(coverage.fundingSourceId)
      ?? i18n.t('coverage.unknownFundingSource'),
    caption: formatPeriod(coverage),
    attention,
    /**
     * One warning icon for the states that mean something is wrong, and quiet text for the ones
     * that merely mean not yet.
     *
     * Three problems share one glyph rather than three labels because the row has room for one
     * short thing and three different sentences competing for it is what squeezed the payer's name
     * to three lines. Which problem it is belongs in the coverage, one tap away, where there is room
     * to say what to do about it. The label still travels for the icon's tooltip and its
     * accessible name, so the distinction survives for anybody who needs it here.
     */
    isProblem,
    // Null when we are billing on it: the row says nothing rather than saying "fine".
    statusLabel: attention === null ? null : i18n.t(`coverage.attention.${attention}`),
    orderNumber,
    rankVariant: orderNumber === null ? 'placeholder' : 'numbered',
  };
} // presentCoverageRow()
